/**
 * SellItemModal.js
 *
 * Modal for selling an item to a team, built for speed during a live auction:
 * - Teams are big tiles (budget left, items bought); tap one. Teams that can't buy are greyed out, with the reason.
 * - The price starts at the starting bid; − / + move it by one bid step, and the quick buttons jump several steps.
 *   It can also be typed (Enter sells).
 * - Checks the lobby settings (bid rules, budget, items per team, items per attribute value) as you go,
 *   and only lets a valid sale through.
 */

import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Minus, Plus, X } from "lucide-react";
import { getTeamItems } from "../library/libraries";
import { getBidProblem, getSaleProblem, getTeamBuyingProblem, roundUpToBid } from "../helpers/saleRules";
import { getTeamColor, sortTeams } from "../assets/teamColors";

const QUICK_STEPS = [1, 5, 10];

const SellItemModal = ({ item, library, teams, settings, onSell, onClose }) => {
    const [selectedTeamID, setSelectedTeamID] = useState(null);
    const [amountText, setAmountText] = useState(String(settings.startingBid));
    const [isSelling, setIsSelling] = useState(false);
    const amountInput = useRef(null);

    const playingTeams = sortTeams(teams).filter((team) => team.members?.length > 0);
    const selectedTeam = playingTeams.find((team) => team.id === selectedTeamID) || null;
    const amount = Number(amountText);
    const details = library.attributes
        .filter((attribute) => attribute.kind !== "name" && attribute.kind !== "score")
        .map((attribute) => item.values?.[attribute.name])
        .filter((value) => value !== undefined && value !== "");

    const problem = amountText === ""
        ? "Enter a price."
        : selectedTeam
            ? getSaleProblem(selectedTeam, item, amount, settings)
            : getBidProblem(amount, settings);
    const canSell = !!selectedTeam && !problem && !isSelling;

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const setAmount = (value) => setAmountText(String(Math.max(settings.startingBid, value)));
    const stepBy = (steps) => {
        const base = Number.isFinite(amount) && amountText !== "" ? roundUpToBid(amount, settings) : settings.startingBid;
        // If a typed price was off the steps, the first step goes to the nearest allowed price
        const offStep = Number.isFinite(amount) && amountText !== "" && base !== amount;
        setAmount(offStep && steps > 0 ? base + (steps - 1) * settings.bidStep : base + steps * settings.bidStep);
    };

    const handleSell = async () => {
        if (!selectedTeam) {
            toast.error("Pick the team that bought it.");
            return;
        }
        if (problem) {
            toast.error(problem);
            return;
        }
        setIsSelling(true);
        const success = await onSell(selectedTeam, amount);
        setIsSelling(false);
        if (success) onClose();
        else toast.error("Failed to sell.");
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-md p-4">
            <div className="relative bg-gradient-to-b from-zinc-800 to-black rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 md:p-8 text-zinc-100 border border-zinc-700">
                <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-200 transition">
                    <X size={22} />
                </button>

                {/* --- Item --- */}
                <div className="text-center">
                    <div className="text-sm uppercase tracking-widest text-zinc-500">Sell</div>
                    <h2 className="mt-1 text-2xl md:text-3xl font-bold text-white">{item.name}</h2>
                    <p className="mt-1 text-sm text-zinc-400">{[...details, `${library.scoreAttribute}: ${item.score}`].join(" • ")}</p>
                </div>

                {/* --- Teams --- */}
                <div className="mt-6 text-sm text-zinc-400">Sold to</div>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {playingTeams.map((team) => {
                        const blocked = getTeamBuyingProblem(team, settings) || getSaleProblem(team, item, settings.startingBid, settings);
                        const isSelected = team.id === selectedTeamID;
                        const color = getTeamColor(team);
                        return (
                            <button
                                key={team.id}
                                type="button"
                                onClick={() => { setSelectedTeamID(team.id); amountInput.current?.focus(); }}
                                disabled={!!blocked}
                                title={blocked || undefined}
                                aria-pressed={isSelected}
                                className={`relative overflow-hidden rounded-xl border-2 p-3 pt-4 text-left transition-colors ${
                                    isSelected ? "border-yellow bg-zinc-800" : "border-zinc-700 bg-zinc-900 hover:border-zinc-500"
                                } disabled:opacity-40 disabled:hover:border-zinc-700`}
                            >
                                <span className={`absolute inset-x-0 top-0 h-1.5 ${color.bg}`} />
                                <span className="block font-semibold text-white">{team.name}</span>
                                <span className="block text-sm text-zinc-400">
                                    ₹{team.budgetLeft ?? 0} left · {getTeamItems(team).length}/{settings.maxItemsPerTeam}
                                </span>
                                {blocked && <span className="mt-1 block text-xs text-coral">{blocked.replace(`${team.name} `, "")}</span>}
                            </button>
                        );
                    })}
                </div>

                {/* --- Price --- */}
                <div className="mt-6 flex items-baseline justify-between text-sm text-zinc-400">
                    <span>Price</span>
                    <span>Starts at ₹{settings.startingBid} · steps of ₹{settings.bidStep}</span>
                </div>
                <div className="mt-2 flex items-stretch gap-3">
                    <button
                        type="button"
                        onClick={() => stepBy(-1)}
                        aria-label={`Down ₹${settings.bidStep}`}
                        className="flex w-16 items-center justify-center rounded-xl border border-zinc-700 bg-zinc-900 hover:bg-zinc-800"
                    >
                        <Minus />
                    </button>
                    <div className="relative flex-1">
                        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-3xl text-zinc-500">₹</span>
                        <input
                            ref={amountInput}
                            type="number"
                            inputMode="numeric"
                            min={settings.startingBid}
                            step={settings.bidStep}
                            value={amountText}
                            onChange={(e) => setAmountText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleSell(); }}
                            className="w-full rounded-xl border border-zinc-700 bg-zinc-900 py-4 pl-11 pr-4 text-center text-4xl font-extrabold text-white focus:outline-none focus:ring-2 focus:ring-yellow"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => stepBy(1)}
                        aria-label={`Up ₹${settings.bidStep}`}
                        className="flex w-16 items-center justify-center rounded-xl border border-zinc-700 bg-zinc-900 hover:bg-zinc-800"
                    >
                        <Plus />
                    </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {QUICK_STEPS.map((steps) => (
                        <button
                            key={steps}
                            type="button"
                            onClick={() => stepBy(steps)}
                            className="flex-1 rounded-lg border border-zinc-700 bg-zinc-900 py-2 text-zinc-200 hover:bg-zinc-800"
                        >
                            +₹{steps * settings.bidStep}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={() => setAmountText(String(settings.startingBid))}
                        className="flex-1 rounded-lg border border-zinc-700 bg-zinc-900 py-2 text-zinc-400 hover:bg-zinc-800"
                    >
                        Reset
                    </button>
                </div>

                <p className={`mt-3 min-h-[1.5rem] text-sm ${problem && (selectedTeam || amountText !== String(settings.startingBid)) ? "text-coral" : "text-zinc-500"}`}>
                    {problem && (selectedTeam || amountText !== String(settings.startingBid)) ? problem : selectedTeam ? "" : "Pick the team that bought it."}
                </p>

                {/* --- Sell --- */}
                <button
                    type="button"
                    onClick={handleSell}
                    disabled={!canSell}
                    className="mt-4 w-full rounded-xl bg-yellow py-4 text-xl font-extrabold text-black transition-colors hover:bg-white disabled:opacity-40 disabled:hover:bg-yellow"
                >
                    {isSelling ? "Selling..." : selectedTeam && !problem ? `Sell to ${selectedTeam.name} for ₹${amount}` : "Sell"}
                </button>
            </div>
        </div>
    );
};

export default SellItemModal;
